import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { billingService } from '@/services/billing.service';
import { setContinuedOnWeb } from '@/lib/mobileGate';
import { useAccountStore } from '@/store/account.store';
import { useSessionStore } from '@/store/session.store';

/**
 * CheckoutComplete — `/checkout/complete`. The success landing for a
 * checkout started from a MOBILE browser (the server sets this as the
 * Stripe `success_url` when the checkout request is flagged `mobile`).
 *
 * It's a self-contained, public page (no AppShell, rendered outside
 * `ProtectedRoute`) so a phone can reach it without tripping the auth /
 * onboarding / mobile gates. Desktop checkouts land on
 * `/account?checkout=success` instead, which already runs its own refresh.
 *
 * Why this page does real work rather than just saying "thanks":
 *
 *  1. **No way back.** The earlier version was a static dead-end — no
 *     button, no link. We give the user an explicit "Continue to
 *     OllieReads" button (and auto-advance once the upgrade is confirmed).
 *
 *  2. **Stale entitlement.** `AccountStore.account` and
 *     `SessionStore.accountBootstrapped` are both persisted in
 *     localStorage, so when the user returns to the app `ProtectedRoute`
 *     serves the cached *pre-upgrade* account and skips the `/auth/callback`
 *     refetch — every paywall prompt keeps firing. We fix that here by
 *     (a) polling `getSubscription()` until the Stripe webhook has mirrored
 *     the new plan, writing it into the store; (b) dropping the bootstrap
 *     latch so the next `ProtectedRoute` mount re-fetches a fresh account;
 *     and (c) marking "continue on web" so the mobile interstitial doesn't
 *     re-interrupt on the way into the Library.
 *
 * Stripe is authoritative; the webhook is async, so the poll is a UX
 * niceness — the user can continue at any time and the background
 * re-bootstrap will reconcile state regardless of the poll outcome.
 */

/** How long to wait between subscription polls. */
const POLL_INTERVAL_MS = 2_000;
/** Stop polling after this long — the webhook is normally a few seconds. */
const POLL_TIMEOUT_MS = 24_000;
/** Brief beat after confirmation so the user registers the success screen. */
const AUTO_ADVANCE_MS = 1_500;

export function CheckoutCompletePage() {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth0();
  const accessToken = useSessionStore((s) => s.accessToken);
  const setAccountBootstrapped = useSessionStore((s) => s.setAccountBootstrapped);
  const setSubscription = useAccountStore((s) => s.setSubscription);

  // `true` once the server reports a paid entitlement (webhook has landed).
  const [confirmed, setConfirmed] = useState(false);
  // Latch so React StrictMode's double-mount doesn't run the one-time
  // store cleanup twice.
  const didPrimeRef = useRef(false);

  const goToApp = useCallback(() => {
    // `replace` so the browser back button can't return to this throwaway
    // confirmation page after the user is in the app.
    navigate('/library', { replace: true });
  }, [navigate]);

  // One-time on mount: dismiss the mobile gate (the user clearly intends to
  // use the web app — they just paid) and drop the bootstrap latch so the
  // next `ProtectedRoute` mount re-fetches the account with its new plan
  // instead of the persisted pre-upgrade cache.
  useEffect(() => {
    if (didPrimeRef.current) return;
    didPrimeRef.current = true;
    setContinuedOnWeb();
    setAccountBootstrapped(false);
  }, [setAccountBootstrapped]);

  // Poll the mirrored subscription until it reports paid (or we time out).
  // Gated on a live access token — it lands asynchronously via
  // <ApiAuthBridge/> once Auth0 restores the session, so firing earlier
  // would just 401.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !accessToken) return;
    if (confirmed) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      try {
        const sub = await billingService.getSubscription();
        if (cancelled) return;
        // Repaint the cached account's subscription slice immediately so the
        // app reflects the new plan even before the re-bootstrap completes.
        setSubscription(sub);
        if (sub.isPaid) {
          setConfirmed(true);
          return;
        }
      } catch {
        // Webhook may not have landed yet, or a transient error — keep
        // retrying until the deadline. Never error-toast on a successful
        // payment.
      }
      if (cancelled || Date.now() >= deadline) return;
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, isAuthenticated, accessToken, confirmed, setSubscription]);

  // Auto-advance into the app once the upgrade is confirmed.
  useEffect(() => {
    if (!confirmed) return;
    const t = setTimeout(goToApp, AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [confirmed, goToApp]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-or-cream-50 px-8 text-center">
      <img src="/brand/ollie_logo_square.png" alt="" width={72} height={72} className="shrink-0" />

      <CheckCircle2 className="h-14 w-14 text-or-green-700" aria-hidden />

      <div className="flex max-w-md flex-col gap-3">
        <h1 className="font-display text-2xl font-bold text-or-orange-800">You're all set!</h1>
        <p className="text-[15px] leading-relaxed text-or-slate-600">
          {confirmed
            ? 'Your upgrade is active. Taking you back to OllieReads…'
            : 'Your payment went through. Tap below to head back to OllieReads and start enjoying your new plan.'}
        </p>
        {!confirmed ? (
          <p className="flex items-center justify-center gap-2 text-[13px] text-or-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Activating your plan — this only takes a moment.
          </p>
        ) : null}
      </div>

      <Button size="lg" className="min-w-[220px]" onClick={goToApp}>
        Continue to OllieReads
      </Button>
    </div>
  );
}
